import type { AppLogger, ThrownObject } from '@core/analytics/interfaces/logger';
import type { SentryApi, SentryQueue } from '@core/analytics/interfaces/sentry';
import type { SentryErrorReport, SentryNoteReport, SentryReport } from '@core/analytics/types/sentry';
import { APP_REPORT_TAG, MAX_PENDING_ERRORS, MAX_PENDING_NOTES } from '../constants/sentry';

export const toError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error !== 'object' || error === null) {
    return new Error(String(error), { cause: error });
  }

  const { message, code } = error as ThrownObject;
  const described = [message, code].find(
    (value): value is string | number => typeof value === 'number' || (typeof value === 'string' && value !== ''),
  );

  return new Error(described === undefined ? 'Non-error value was thrown' : `${described}`, { cause: error });
};

const sentErrors = new WeakSet<object>();

export const sendReport = (sentry: SentryApi, report: SentryReport) => {
  try {
    if (report.kind === 'note') {
      sentry.addNote(report.note);
      return;
    }

    if (report.kind === 'user') {
      sentry.setUser(report.user);
      return;
    }

    const { error, context } = report;
    const { cause } = error;

    if (sentErrors.has(error) || (typeof cause === 'object' && cause !== null && sentErrors.has(cause))) {
      return;
    }

    sentErrors.add(error);

    sentry.withScope((scope) => {
      Object.entries(context?.tags ?? {}).forEach(([key, value]) => scope.setTag(key, value));
      Object.entries(context?.extra ?? {}).forEach(([key, value]) => scope.setExtra(key, value));

      if (context?.fingerprint) {
        scope.setFingerprint(context.fingerprint);
      }

      sentry.captureException(error);
    });
  } catch (error) {
    if (import.meta.dev) {
      console.warn('[sentry] report failed:', error);
    }
  }
};

export const createConsoleLogger = (): AppLogger => ({
  error: (error, context) => {
    console.error('[AppError]', error, context);
  },

  note: (note) => {
    if (import.meta.dev) {
      console.debug('[AppLogNote]', note);
    }
  },
});

export const createSentryLogger = (onReport: (report: SentryReport) => void): AppLogger => ({
  error(error, context) {
    const reportedError = toError(error);

    if (import.meta.dev) {
      console.error('[AppError]', reportedError, context);
    }

    onReport({
      kind: 'error',
      error: reportedError,
      context: { ...context, tags: { ...context.tags, [APP_REPORT_TAG.name]: APP_REPORT_TAG.value } },
    });
  },

  note({ cause, ...note }) {
    const described = cause === undefined
      ? note
      : { ...note, data: { ...note.data, reason: toError(cause).message } };

    if (import.meta.dev) {
      console.debug('[AppLogNote]', described);
    }

    onReport({ kind: 'note', note: described });
  },
});

export const createQueue = (): SentryQueue => {
  const errors: SentryErrorReport[] = [];
  const notes: SentryNoteReport[] = [];
  let user: SentryReport | null = null;

  return {
    add(report) {
      if (report.kind === 'user') {
        user = report;
        return;
      }

      if (report.kind === 'note') {
        if (notes.length < MAX_PENDING_NOTES) {
          notes.push(report);
        }

        return;
      }

      if (errors.length < MAX_PENDING_ERRORS) {
        errors.push(report);
      }
    },

    drain() {
      const held = [...(user ? [user] : []), ...notes, ...errors];

      user = null;
      notes.length = 0;
      errors.length = 0;

      return held;
    },
  };
};
